import { defineStore } from 'pinia'
import { authService } from '@/api/auth'
import { useLoadingStore } from '@/stores/loading'

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  username: string
  role: 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE'
  owner_id: string | null
  phone_number?: string
  location?: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
}

const getSavedUser = (): User | null => {
  try {
    const saved = localStorage.getItem('user')
    return saved ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: getSavedUser(),
    isAuthenticated: !!localStorage.getItem('accessToken'),
  }),
  getters: {
    isSupervisor: (state) =>
      ['SUPERVISOR', 'ADMIN', 'SYSTEM_ADMIN'].includes(
        (state.user?.role ?? '').toString().toUpperCase()
      ),
    isAdmin: (state) =>
      ['ADMIN', 'SYSTEM_ADMIN'].includes((state.user?.role ?? '').toString().toUpperCase()),
  },
  actions: {
    async login(email: string, password: string) {
      const loading = useLoadingStore()
      loading.start()
      try {
        const data = await authService.login(email, password)
        localStorage.setItem('accessToken', data.access)
        localStorage.setItem('refreshToken', data.refresh)
        localStorage.setItem('user', JSON.stringify(data.user))
        this.user = data.user
        this.isAuthenticated = true
        return data.user.role
      } catch (error) {
        console.error('Login failed:', error)
        throw error
      } finally {
        loading.stop()
      }
    },

    async register(userData: {
      username: string
      email: string
      password: string
      confirmPassword: string
      first_name: string
      last_name: string
    }) {
      try {
        return await authService.register(userData)
      } catch (error) {
        console.error('Registration failed:', error)
        throw error
      }
    },

    async logout() {
      const loading = useLoadingStore()
      loading.start()
      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          await authService.logout(refreshToken)
        }
      } catch (error) {
        console.error('Logout failed:', error)
      } finally {
        this.clearAuth()
        loading.stop()
        // Force redirect to login
        window.location.href = '/login'
      }
    },

    clearAuth() {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      this.user = null
      this.isAuthenticated = false
    },

    async fetchUserProfile() {
      const loading = useLoadingStore()
      loading.start()
      try {
        const data = await authService.getUserProfile()
        localStorage.setItem('user', JSON.stringify(data))
        this.user = data
        this.isAuthenticated = true
        return data
      } catch (error: any) {
        if (error?.response?.status === 401) {
          this.clearAuth()
        }
        throw error
      } finally {
        loading.stop()
      }
    },

    async checkAuth(): Promise<void> {
      const accessToken = localStorage.getItem('accessToken')
      const refreshToken = localStorage.getItem('refreshToken')

      if (!accessToken || !refreshToken) {
        this.clearAuth()
        return
      }

      try {
        const data = await authService.getUserProfile()
        localStorage.setItem('user', JSON.stringify(data))
        this.user = data
        this.isAuthenticated = true
      } catch (error: any) {
        // Only clear auth on 401 (token refresh failure)
        // Never clear session on transient network or server errors
        if (error?.response?.status === 401) {
          this.clearAuth()
        }
        console.warn('[checkAuth] Could not refresh profile:', error)
      }
    },

    async requestPasswordReset(email: string) {
      const loading = useLoadingStore()
      loading.start()
      try {
        return await authService.requestPasswordReset(email)
      } finally {
        loading.stop()
      }
    },

    async confirmPasswordReset(data: { uid: string; token: string; password: string; confirmPassword: string }) {
      const loading = useLoadingStore()
      loading.start()
      try {
        return await authService.confirmPasswordReset(data)
      } finally {
        loading.stop()
      }
    },
  },
})

